import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { IInternalUserItem, IProviderSearchResult, IInternalUserQuery } from '@core/model';
import { UsersService } from '@core/services';
import { TableColumn } from '@mynexus/mynexus-ui-lib';
import { isEmpty } from 'lodash-es';
import { LazyLoadEvent, MessageService, SelectItem } from 'primeng/api';
import { Table } from 'primeng/table';
import { finalize, timeout } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-internal-users-list',
    templateUrl: './internal-users-list.component.html',
    styleUrls: ['./internal-users-list.component.scss'],
    standalone: false
})
export class InternalUsersListComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  @ViewChild('dt', { static: true }) dataTable!: Table;

  readonly amountOfRows = 10;
  readonly defaultSortField = 'displayName';
  readonly defaultSortOrder = 1;
  readonly defaultSortOrderOnLoad = 1;

  cols: TableColumn[];
  isLoading = true;
  totalRecords = 0;
  users: IInternalUserItem[] = [];

  email = '';
  displayName = '';
  isActive?: boolean;
  activesList: SelectItem[] = [];
  serverResponseRecieved = false;
  filtersData: string[] = [];
  internalUserFilter: IInternalUserQuery = { displayName: '', email: '', isActive: undefined, page: 0, sort: '', top: this.amountOfRows };

  constructor(
    private usersService: UsersService,
    private router: Router,
    private messageService: MessageService
  ) {
    this.cols = [
      new TableColumn('', '', '5.3rem'),
      new TableColumn('Business Email', 'email'),
      new TableColumn('Display Name', 'displayName'),
      new TableColumn('Active', 'isApproved', '20rem')
    ];
  }

  ngOnInit(): void {
    this.activesList = [
      { value: true, label: 'Active' },
      { value: false, label: 'Inactive' }
    ];
    this.isLoading = true;
  }

  onRowExpand(event: any): void {
    this.dataTable.expandedRowKeys = { [event.data.email]: true };
  }

  resetFilters(): void {
    this.dataTable.filters = {};
    this.dataTable.clear();
    this.dataTable.sort({ field: this.defaultSortField });
    this.dataTable.first = 0;
    this.email = '';
    this.displayName = '';
    this.isActive = undefined;
  }


  getUsers(event: LazyLoadEvent): void {
    this.isLoading = true;
    this.buildAdditionalFilters(event);
    this.users = [];

    var queryParams:Record<any, any> = {
      isExternal: false,
      displayName: this.internalUserFilter.displayName,
      email: this.internalUserFilter.email,
      page: this.internalUserFilter.page,
      sort: this.internalUserFilter.sort,
      top: this.internalUserFilter.top,
      take: event.rows,
      skip: event.first,
      orderByDescending: event.sortOrder && event.sortOrder > 0 ? false : true,
      orderBy: event.sortField
    };
    if(this.internalUserFilter.isActive !== undefined){
      queryParams.isApproved = this.internalUserFilter.isActive;
    }

    var queryString = new URLSearchParams(queryParams).toString();
    if(queryParams.orderBy != null){
      this.usersService
        .getInternalUsers(queryString)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => (this.isLoading = false)),
        )
      .subscribe({
        next: (res: IProviderSearchResult<IInternalUserItem>) => {
          if (res) {
            this.users = res.items;
            this.totalRecords = res.count;
            this.isLoading = false;
          }
        },
        error: error => {
          this.messageService.add({
            severity: 'error',
            detail: error.message,
            life: 5000
          });
        }
      });
    }
  }

  editUser(userId: string): void {
    this.router.navigate(['internalusers', userId, 'details']);
  }

  private isFilterEmpty(): boolean {
    return isEmpty(this.dataTable.filters);
  }

  isClearAllEnabled(): boolean {
    const isSortApplied = !(
      this.dataTable.sortOrder !== this.defaultSortOrderOnLoad ||
      this.dataTable.sortField !== this.defaultSortField
    );

    return isSortApplied && this.isFilterEmpty();
  }

  applyFilterTableValue(event: any, criteria: string, matchMode: string): void {
    this.dataTable.filter(event, criteria, matchMode);
  }

  onUserStatusChanged(): void {
    const metadata = this.dataTable.createLazyLoadMetadata();
    this.getUsers(metadata);
  }

  fetchSuggestions(value: string, prop: string): void {
    const userInput = (value ?? '').toLocaleLowerCase();
    const query = `searchField=${prop}&keyword=${userInput}&isExternal=false`;

    this.usersService
      .getUsersRelevantData(query)
      .pipe(
        timeout(30000),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
      next: (items) => {
        this.filtersData = items;
      },
      error: (err) => {
        if (err.name === 'TimeoutError') {
          this.messageService.add({
            severity: 'error',
            detail: 'Request timed out. Please try again later.',
            life: 5000
          });
        } else {
          this.messageService.add({
            severity: 'error',
            detail: err.message || 'An error occurred.',
            life: 5000
          });
        }
      }
    });
  }

  onAutocompleteSelect($event: any, dataKey: string): void {
    const filterValue = (typeof $event.value === 'object' && $event.value !== null)
      ? $event.value[dataKey]
      : $event.value;

    this.dataTable.filter(filterValue, dataKey, 'in');
  }

  onClearAutocomplete(column: string): void {
    this.dataTable.filter(null, column, 'contains');
    this.filtersData = [];
  }

  // adds additional filter parameters based on lazyLoad event details
  private buildAdditionalFilters(event: LazyLoadEvent){
    // Set page using the event first data
    // first is equivalent to skip, but we do not currently support skip, only page
    // So we have to calculate the page number.
    this.internalUserFilter.page = ((event.first ?? 0) / this.amountOfRows) + 1;

    // reset filters, and readd as needed
    this.internalUserFilter.displayName = '';
    this.internalUserFilter.email = '';
    this.internalUserFilter.isActive = undefined;

    // Filters below will need to match names as defined in HTML, and used in applyFilterTableValue()
    if (event.filters) {
      if (event.filters['displayName']) {
        this.internalUserFilter.displayName = event.filters['displayName'].value;
      }
      if (event.filters['email']) {
        this.internalUserFilter.email = event.filters['email'].value;
      }
      if (event.filters['isApproved']) {
        this.internalUserFilter.isActive = event.filters['isApproved'].value;
      }
    }
  }

}
